import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { CreatePartnerLeadRequest } from '@bliks-io/contracts';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { translateErrorCode } from '@/lib/errors';
import { useCreatePartnerLead } from '@/pages/partner/api/mutations/usePartnerLeads';
import { NewLeadDialog } from './NewLeadDialog';
import type { NewLeadPrefill } from './newLeadDefaults';

interface NewLeadFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: NewLeadPrefill;
  /** The call-list contact the lead comes from; the backend marks it as submitted. */
  contactId?: string;
  onCreated?: () => void;
}

/** The lead dialog wired to the create mutation and its success/error feedback. */
export function NewLeadFlow({
  open,
  onOpenChange,
  prefill,
  contactId,
  onCreated
}: NewLeadFlowProps) {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | undefined>();
  const createLead = useCreatePartnerLead();

  const handleSubmit = (lead: CreatePartnerLeadRequest) => {
    setSubmitError(undefined);
    createLead.mutate(contactId ? { ...lead, contactId } : lead, {
      onSuccess: () => {
        toast.success(t('partner.newLead.successToast'), {
          description: t('partner.newLead.successDescription'),
          duration: 8000
        });
        onOpenChange(false);
        onCreated?.();
      },
      onError: (error) => {
        // Also kept inside the dialog: it stays open on failure, and the
        // appointment is already booked, so the reason must not scroll away
        // with a toast.
        setSubmitError(translateErrorCode(error, 'partner.newLead.errorToast', t));
        toastApiError(error, 'partner.newLead.errorToast');
      }
    });
  };

  return (
    <NewLeadDialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setSubmitError(undefined);
      }}
      onSubmit={handleSubmit}
      isSubmitting={createLead.isPending}
      error={submitError}
      onErrorDismiss={() => setSubmitError(undefined)}
      prefill={prefill}
    />
  );
}
